import { useState } from 'react';
import { UtilityDottedFilterMediumIcon } from '@mweb/zappos-ui/Icons';

import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { isSingleSelectFacet } from 'components/search/FacetAccordion/FacetAccordion.utils';
import Button from 'components/common/Button';
import useMartyContext from 'hooks/useMartyContext';
import { SelectedFilterList } from 'components/search/SelectedFilterList/SelectedFilterList';
import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import CaretLeftIcon from 'components/common/IconsV2/CaretLeftIcon';
import { Drawer } from 'components/common/Drawer';
import { BRAND_NAME_FACET, REAL_BRAND_FACET } from 'helpers/SearchUtils';
import type { BreadcrumbType, FacetDataType, FacetValueType } from 'types/search';
import IconButton from 'components/common/IconButton';
import { pluralize, yieldToMain } from 'helpers/index';
import { cn } from 'helpers/classnames';

const DETAIL_VIEW_FACETS = {
  [BRAND_NAME_FACET]: true,
  [REAL_BRAND_FACET]: true
} as const;

type FacetsDrawerProps = {
  totalProductCount: number;
  clearFilters: () => void;
  isFacetsVisible: boolean;
  toggleFacetsContainer: (visible: boolean) => void;
};

export const FacetsDrawer = ({ totalProductCount, clearFilters, isFacetsVisible, toggleFacetsContainer }: FacetsDrawerProps) => {
  const { testId } = useMartyContext();
  const [detailFacet, setDetailFacet] = useState<string | null>(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const {
    state: { facets, autoComplete, breadcrumbs, isLoading },
    actions: { handleBreadcrumbPillClick, handleFacetSelect, trackFacetSearch },
    meta: { navigationVersion }
  } = useFacetsContext();

  // Each consumer manages its own accordion expanded state locally.
  const [expandedFacets, setExpandedFacets] = useState<Set<string>>(
    () => new Set(facets.filter(facet => facet.isExpanded).map(facet => facet.facetField))
  );

  const handleOpenChange = async (open: boolean) => {
    await yieldToMain();
    setShouldAnimate(true);
    toggleFacetsContainer(open);
  };

  const makeBreadcrumbClickHandler = (crumb: BreadcrumbType) => (e: React.MouseEvent) => {
    e.preventDefault();

    if (isLoading) {
      return;
    }

    handleBreadcrumbPillClick(crumb);
  };

  // Handle accordion changes with detail view logic
  const handleDrawerAccordionChange = (expandedValues: string[]): void => {
    if (isLoading) {
      return;
    }

    const currentExpanded = [...expandedFacets];
    const added = expandedValues.find(value => !currentExpanded.includes(value));

    if (added && DETAIL_VIEW_FACETS[added as keyof typeof DETAIL_VIEW_FACETS]) {
      setDetailFacet(added);
      return;
    }

    setExpandedFacets(new Set(expandedValues));
  };

  const handleDetailFacetSelect = (facet: FacetDataType, facetValue: FacetValueType) => {
    if (isLoading) {
      return;
    }

    const facetIndex = facets.findIndex(f => f.facetField === facet.facetField);
    const valueIndex = facet.values.findIndex(v => v.name === facetValue.name);

    handleFacetSelect({
      facetGroup: facet.facetField,
      facetName: facetValue.name,
      selectedFacetGroupIndex: facetIndex,
      selectedFacetIndex: valueIndex,
      isSelected: facetValue?.selected || false,
      facetValue
    });

    // Auto-collapse single-select facets (e.g., Gender, Department) after selection
    if (!facetValue?.selected && isSingleSelectFacet(facet) && navigationVersion !== 'v2p2') {
      setExpandedFacets(prev => {
        const next = new Set(prev);
        next.delete(facet.facetField);
        return next;
      });
    }
  };

  const handleClearFilters = async () => {
    if (isLoading) {
      return;
    }

    await yieldToMain();
    clearFilters();
  };

  // Get the current facet for title display
  const currentFacet = detailFacet ? facets.find(f => f.facetField === detailFacet) : null;
  const drawerTitle = currentFacet ? currentFacet.facetFieldDisplayName : 'Filters';

  const filtersCount = breadcrumbs?.length;

  return (
    <Drawer.Root open={isFacetsVisible} onOpenChange={handleOpenChange}>
      <Drawer.Trigger
        className="body-xs-subtle flex h-10 w-auto items-center justify-center gap-2 rounded-sm border border-gray-200 px-4 max-sm:w-full"
        data-test-id={testId('filterButton')}
        onClick={() => handleOpenChange(true)}
      >
        <UtilityDottedFilterMediumIcon size={24} />
        Filters {filtersCount > 0 ? `(${filtersCount})` : ''}
      </Drawer.Trigger>

      <Drawer.Content
        shouldAnimate={shouldAnimate}
        className={cn('h-[98dvh]', { 'pointer-events-none': isLoading })}
        data-test-id={testId('mobileFacetDrawer')}
      >
        <Drawer.Header
          previousSlot={
            detailFacet && (
              <IconButton
                label={'Back'}
                size="base"
                variant="icon"
                className="flex aspect-square size-8 min-w-0 shrink-0 justify-self-end"
                onClick={() => setDetailFacet(null)}
                data-test-id={testId('mobileFacetDrawerBackButton')}
              >
                <CaretLeftIcon size={24} />
              </IconButton>
            )
          }
        >
          {drawerTitle}
        </Drawer.Header>

        <div className={cn('grow overflow-y-auto px-5 py-4 transition-opacity', { 'opacity-50': isLoading })}>
          {detailFacet ? (
            // Detail view for specific facets (e.g., Brand)
            (() => {
              const facet = facets.find(f => f.facetField === detailFacet);
              if (!facet) return null;

              return (
                <FacetAccordion.Root type="multiple" value={[facet.facetField]}>
                  <FacetAccordion.Item value={facet.facetField} facet={facet} context="mobile">
                    <FacetAccordion.Content>
                      <FacetAccordion.AutoComplete onChange={e => trackFacetSearch(facet, e.target.value)} />

                      <FacetAccordion.ValueList
                        autoComplete={autoComplete}
                        navigationVersion={navigationVersion}
                        className="max-h-none overflow-visible"
                      >
                        {({ facetValue, facet: activeFacet }) => (
                          <FacetAccordion.Value
                            data-test-id={testId(`mobileFacet-${facetValue.name}`)}
                            facetValue={facetValue}
                            navigationVersion={navigationVersion}
                            onFacetSelect={() => handleDetailFacetSelect(activeFacet, facetValue)}
                          />
                        )}
                      </FacetAccordion.ValueList>
                    </FacetAccordion.Content>
                  </FacetAccordion.Item>
                </FacetAccordion.Root>
              );
            })()
          ) : (
            // Normal accordion view
            <FacetAccordion.Root type="multiple" value={[...expandedFacets]} onValueChange={handleDrawerAccordionChange}>
              {facets.map(facet => {
                const { facetField, facetFieldDisplayName } = facet;

                return (
                  <FacetAccordion.Item
                    key={facetField}
                    value={facetField}
                    data-test-id={testId(`facetGroup-${facetFieldDisplayName}`)}
                    facet={facet}
                    context="mobile"
                  >
                    <FacetAccordion.Trigger data-test-id-facet-head-name={facetFieldDisplayName}>
                      <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
                      <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
                    </FacetAccordion.Trigger>

                    <FacetAccordion.SeoLinks />

                    <FacetAccordion.Content>
                      <FacetAccordion.AutoComplete onChange={e => trackFacetSearch(facet, e.target.value)} />

                      <FacetAccordion.ValueList
                        data-test-id={testId(`${facetFieldDisplayName}-values`)}
                        autoComplete={autoComplete}
                        navigationVersion={navigationVersion}
                        className="max-h-none"
                      >
                        {({ facetValue, facet: activeFacet }) => (
                          <FacetAccordion.Value
                            data-test-id={testId(`facetBox-${facetValue.name}`)}
                            facetValue={facetValue}
                            navigationVersion={navigationVersion}
                            onFacetSelect={() => handleDetailFacetSelect(activeFacet, facetValue)}
                          />
                        )}
                      </FacetAccordion.ValueList>
                    </FacetAccordion.Content>
                  </FacetAccordion.Item>
                );
              })}
            </FacetAccordion.Root>
          )}
        </div>

        <Drawer.Footer className="flex flex-col items-center gap-2">
          {breadcrumbs.length > 0 && (
            <SelectedFilterList.Root className="scrollbar-hidden -mx-5 flex-row items-center gap-4 self-stretch overflow-x-auto px-5">
              <SelectedFilterList.ClearButton onClick={handleClearFilters} disabled={isLoading} data-test-id={testId('clearFilters')}>
                Clear Filters
              </SelectedFilterList.ClearButton>
              <SelectedFilterList.FilterItems className="max-md:overflow-visible max-md:overscroll-auto max-md:px-0">
                {breadcrumbs.map((crumb, index) => (
                  <SelectedFilterList.FilterItem
                    key={`${crumb.name}-${index}`}
                    removeUrl={crumb.removeUrl}
                    onClick={makeBreadcrumbClickHandler(crumb)}
                    data-test-id={testId(`chip-${crumb.name}`)}
                  >
                    {crumb.name}
                  </SelectedFilterList.FilterItem>
                ))}
              </SelectedFilterList.FilterItems>
            </SelectedFilterList.Root>
          )}

          <Drawer.Close asChild>
            <Button className="w-full [&_span]:items-baseline [&_span]:gap-1" data-test-id={testId('showResults')} height="small">
              Show results -{' '}
              <span className="text-xs zappos:font-normal zappos:normal-case">
                {totalProductCount} {pluralize('Item', totalProductCount)}
              </span>
            </Button>
          </Drawer.Close>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer.Root>
  );
};
